import {CompanyInfoSection} from '@frontend/shared-api';
import {
  CompanyInfoFieldName,
  CompanyInfoValues,
  CONTINGENT_CATEGORY,
  HR_CATEGORY,
  INDUSTRIAL_CATEGORY,
  SALARIED_CATEGORY,
  SECTION_DEFS,
  SectionDef,
  SectionStatus,
  UnitKpis,
  WorkforceCategoryDef,
} from './step-1-1-company-information.types';

export type BenchmarkPosition = 'below' | 'within' | 'above';

export interface VisibleShares<Segment> {
  visible: Segment[];
  total: number;
  shareByKey: Map<string, number>;
}

export function sectionFields(section: SectionDef): CompanyInfoFieldName[] {
  return [
    ...section.selectFields,
    ...section.categories.flatMap((category) => [
      category.headcountFullTime,
      category.headcountPartTime,
      category.fte,
    ]),
    ...section.countFields,
  ];
}

export function requiredSectionFields(
  section: SectionDef,
  tariffRequired: boolean,
): CompanyInfoFieldName[] {
  return sectionFields(section).filter((field) => field !== 'tariffId' || tariffRequired);
}

export function isFilled(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  return typeof value === 'number' ? !Number.isNaN(value) : value.trim() !== '';
}

export function sectionStatus(
  values: CompanyInfoValues,
  section: SectionDef,
  tariffRequired: boolean,
): SectionStatus {
  const fields = requiredSectionFields(section, tariffRequired);
  const filledCount = fields.filter((field) => isFilled(values[field])).length;
  if (filledCount === 0) {
    return 'empty';
  }
  return filledCount === fields.length ? 'complete' : 'in-progress';
}

export function completedSectionCount(values: CompanyInfoValues, tariffRequired: boolean): number {
  return SECTION_DEFS.filter(
    (section) => sectionStatus(values, section, tariffRequired) === 'complete',
  ).length;
}

export function unitStatus(values: CompanyInfoValues, tariffRequired: boolean): SectionStatus {
  const statuses = SECTION_DEFS.map((section) => sectionStatus(values, section, tariffRequired));
  if (statuses.every((status) => status === 'complete')) {
    return 'complete';
  }
  return statuses.every((status) => status === 'empty') ? 'empty' : 'in-progress';
}

export function isSectionDirty(
  current: CompanyInfoValues,
  baseline: CompanyInfoValues,
  section: SectionDef,
): boolean {
  return sectionFields(section).some(
    (field) => normalizeValue(current[field]) !== normalizeValue(baseline[field]),
  );
}

export function pickSectionValues(
  values: CompanyInfoValues,
  section: SectionDef,
): Partial<CompanyInfoValues> {
  return Object.fromEntries(sectionFields(section).map((field) => [field, values[field]]));
}

export function sectionByName(name: CompanyInfoSection): SectionDef | undefined {
  return SECTION_DEFS.find((section) => section.name === name);
}

export function categoryHeadcount(
  values: CompanyInfoValues,
  category: WorkforceCategoryDef,
): number {
  return (
    numberOrZero(values[category.headcountFullTime]) +
    numberOrZero(values[category.headcountPartTime])
  );
}

export function categoryFte(values: CompanyInfoValues, category: WorkforceCategoryDef): number {
  return numberOrZero(values[category.fte]);
}

export function fteExceedsHeadcount(
  values: CompanyInfoValues,
  category: WorkforceCategoryDef,
): boolean {
  const fte = values[category.fte];
  if (!isFilled(fte)) {
    return false;
  }
  return numberOrZero(fte) > categoryHeadcount(values, category);
}

export function otherHeadcount(values: CompanyInfoValues): number {
  return numberOrZero(values.pensioners) + numberOrZero(values.inactiveEmployees);
}

export function headcountWithoutHr(values: CompanyInfoValues): number {
  return (
    categoryHeadcount(values, SALARIED_CATEGORY) +
    categoryHeadcount(values, INDUSTRIAL_CATEGORY) +
    categoryHeadcount(values, CONTINGENT_CATEGORY) +
    otherHeadcount(values)
  );
}

export function unitKpis(values: CompanyInfoValues): UnitKpis {
  const salariedHeadcount = categoryHeadcount(values, SALARIED_CATEGORY);
  const industrialHeadcount = categoryHeadcount(values, INDUSTRIAL_CATEGORY);
  const salariedFte = categoryFte(values, SALARIED_CATEGORY);
  const industrialFte = categoryFte(values, INDUSTRIAL_CATEGORY);
  return {
    headcount: {
      total: salariedHeadcount + industrialHeadcount,
      salaried: salariedHeadcount,
      industrial: industrialHeadcount,
    },
    fte: {
      total: roundFte(salariedFte + industrialFte),
      salaried: salariedFte,
      industrial: industrialFte,
    },
  };
}

export function sumValues(valuesList: CompanyInfoValues[]): CompanyInfoValues {
  const sum = (field: CompanyInfoFieldName): number =>
    valuesList.reduce((total, values) => total + numberOrZero(values[field]), 0);
  return {
    industryId: null,
    tariffId: null,
    salariedHeadcountFullTime: sum('salariedHeadcountFullTime'),
    salariedHeadcountPartTime: sum('salariedHeadcountPartTime'),
    salariedFte: roundFte(sum('salariedFte')),
    industrialHeadcountFullTime: sum('industrialHeadcountFullTime'),
    industrialHeadcountPartTime: sum('industrialHeadcountPartTime'),
    industrialFte: roundFte(sum('industrialFte')),
    hrHeadcountFullTime: sum('hrHeadcountFullTime'),
    hrHeadcountPartTime: sum('hrHeadcountPartTime'),
    hrFte: roundFte(sum('hrFte')),
    contingentHeadcountFullTime: sum('contingentHeadcountFullTime'),
    contingentHeadcountPartTime: sum('contingentHeadcountPartTime'),
    contingentFte: roundFte(sum('contingentFte')),
    pensioners: sum('pensioners'),
    inactiveEmployees: sum('inactiveEmployees'),
  };
}

export function careRatio(totals: CompanyInfoValues): number | null {
  const hrFte = categoryFte(totals, HR_CATEGORY);
  if (hrFte <= 0) {
    return null;
  }
  return Math.round((headcountWithoutHr(totals) / hrFte) * 10) / 10;
}

export function benchmarkPosition(
  ratio: number,
  typicalMin: number,
  typicalMax: number,
): BenchmarkPosition {
  if (ratio < typicalMin) {
    return 'below';
  }
  return ratio > typicalMax ? 'above' : 'within';
}

export function visibleShares<Segment extends {key: string; value: number}>(
  segments: Segment[],
  hiddenKeys: ReadonlySet<string>,
): VisibleShares<Segment> {
  const visible = segments.filter((segment) => !hiddenKeys.has(segment.key));
  const total = visible.reduce((sum, segment) => sum + segment.value, 0);
  const shareByKey = new Map(
    visible.map((segment) => [segment.key, total > 0 ? (segment.value / total) * 100 : 0]),
  );
  return {visible, total, shareByKey};
}

function numberOrZero(value: string | number | null | undefined): number {
  return typeof value === 'number' && !Number.isNaN(value) ? value : 0;
}

function roundFte(value: number): number {
  return Math.round(value * 100) / 100;
}

function normalizeValue(value: string | number | null | undefined): string | number | null {
  return isFilled(value) ? (value ?? null) : null;
}
